import { CustomEventType } from '../events/CustomEventType.enum';
import { dispatchCustomEvent } from '../events/dispatchCustomEvent';
import Cookies from 'js-cookie';
import { LogEvent } from '../types/LogEvent.type';
import { OPT_IN_GROUPS, OPT_OUT_GROUPS, GLOBAL_GROUPS_OPT_IN, GLOBAL_GROUPS_OPT_OUT, DPM_COOKIE_NAME, DPM_COOKIE_EXPIRATION_DAYS, DPM_LEGACY_COOKIE_NAME, DPM_ALERT_BOX_CLOSED_COOKIE_NAME } from './constants';
export class DataPrivacyManager {
    constructor() {
        this.history = [];
        this.lastOptInCheck = false;
    }
    getCookieDomain(hostname = window.location.hostname) {
        const parts = hostname.split('.').filter(Boolean);
        if (parts.length < 2) {
            return undefined;
        }
        return `.${parts.slice(-2).join('.')}`;
    }
    static getInstance() {
        if (typeof globalThis !== 'undefined') {
            const g = globalThis;
            g.DATA_PRIVACY_MANAGER ??= new DataPrivacyManager();
            return g.DATA_PRIVACY_MANAGER;
        }
        // fallback for very old environments
        DataPrivacyManager.instance ??= new DataPrivacyManager();
        return DataPrivacyManager.instance;
    }
    // called when the page is loaded to honor existing consent state
    initialize() {
        this.logMessage(LogEvent.INITIALIZE);
        let savedObj = this.readCookie();
        // no dpm record exists, check for legacy cookie and migrate if found, then re-check for dpm cookie
        if (!savedObj) {
            this.migrateLegacyCookie();
            savedObj = this.readCookie();
        }
        // opt out via gpc signal
        const gpc = this.gpcCheck();
        if (gpc) {
            this.logMessage(LogEvent.OPT_OUT);
            this.lastOptInCheck = false;
            this.dispatchOptOut();
            this.setGlobalGroups(GLOBAL_GROUPS_OPT_OUT);
            this.writeCookie({
                gpc: true,
                optOut: true,
                selectionSource: 'user',
                timestamp: new Date().toISOString()
            });
            return;
        }
        // opt out via cookie
        if (savedObj?.optOut) {
            this.logMessage(LogEvent.OPT_OUT);
            this.lastOptInCheck = false;
            this.dispatchOptOut();
            this.setGlobalGroups(GLOBAL_GROUPS_OPT_OUT);
            return;
        }
        // opt in via cookie or default
        this.logMessage(LogEvent.OPT_IN);
        this.lastOptInCheck = true;
        this.dispatchOptIn();
        this.setGlobalGroups(GLOBAL_GROUPS_OPT_IN);
        // if no record exists save
        if (!savedObj) {
            this.writeCookie({
                gpc: this.gpcCheck(),
                optOut: false,
                selectionSource: 'auto',
                timestamp: new Date().toISOString()
            });
        }
    }
    optIn(userSelected = false) {
        this.logMessage(LogEvent.OPT_IN);
        this.lastOptInCheck = true;
        this.dispatchOptIn();
        this.setGlobalGroups(GLOBAL_GROUPS_OPT_IN);
        this.writeCookie({
            gpc: this.gpcCheck(),
            optOut: false,
            selectionSource: userSelected ? 'user' : 'auto',
            timestamp: new Date().toISOString()
        });
        this.updateLegacyCookie(true);
    }
    updateLegacyCookie(optedIn) {
        const domain = this.getCookieDomain();
        const legacyCookie = Cookies.get(DPM_LEGACY_COOKIE_NAME);
        if (legacyCookie) {
            // Parse key=value&key=value format, split only on first '='
            const pairs = legacyCookie.split('&').map(pair => {
                const idx = pair.indexOf('=');
                return idx === -1
                    ? [pair, '']
                    : [pair.slice(0, idx), pair.slice(idx + 1)];
            });
            let updated = false;
            const newPairs = pairs.map(([k, v]) => {
                if (k === 'groups') {
                    const decodedGroups = decodeURIComponent(v);
                    if (optedIn && decodedGroups.includes('C0004:0')) {
                        updated = true;
                        const replaced = decodedGroups
                            .replace(/C0004:0/g, 'C0004:1')
                            .replace(/C0003:0/g, 'C0003:1');
                        return [k, replaced];
                    }
                    if (!optedIn && decodedGroups.includes('C0004:1')) {
                        updated = true;
                        const replaced = decodedGroups
                            .replace(/C0004:1/g, 'C0004:0')
                            .replace(/C0003:1/g, 'C0003:0');
                        return [k, replaced];
                    }
                }
                return [k, v];
            });
            if (updated) {
                const newCookie = newPairs.map(([k, v]) => `${k}=${v}`).join('&');
                Cookies.set(DPM_LEGACY_COOKIE_NAME, newCookie, {
                    sameSite: 'lax',
                    expires: DPM_COOKIE_EXPIRATION_DAYS,
                    ...(domain ? { domain } : {})
                });
                this.logMessage(optedIn
                    ? LogEvent.LEGACY_COOKIE_UPDATED_OPTED_IN
                    : LogEvent.LEGACY_COOKIE_UPDATED_OPTED_OUT);
                Cookies.set(DPM_ALERT_BOX_CLOSED_COOKIE_NAME, new Date().toISOString(), {
                    sameSite: 'lax',
                    expires: DPM_COOKIE_EXPIRATION_DAYS,
                    ...(domain ? { domain } : {})
                });
                return;
            }
        }
        else {
            const datestamp = new Date().toString().split(' ').join('+');
            const allGroups = GLOBAL_GROUPS_OPT_IN.split(',').filter(Boolean);
            const activeSet = new Set((optedIn ? GLOBAL_GROUPS_OPT_IN : GLOBAL_GROUPS_OPT_OUT)
                .split(',')
                .filter(Boolean));
            const groupsValue = allGroups
                .map(g => `${g}:${activeSet.has(g) ? 1 : 0}`)
                .join(',');
            const cookieValue = [
                `landingPath=NotLandingPage`,
                `datestamp=${datestamp}`,
                `version=202510.1.0`,
                `groups=${groupsValue}`,
                `hosts=`
            ].join('&');
            Cookies.set(DPM_LEGACY_COOKIE_NAME, cookieValue, {
                sameSite: 'lax',
                expires: DPM_COOKIE_EXPIRATION_DAYS,
                ...(domain ? { domain } : {})
            });
            this.logMessage(optedIn
                ? LogEvent.LEGACY_COOKIE_CREATED_OPTED_IN
                : LogEvent.LEGACY_COOKIE_CREATED_OPTED_OUT);
            Cookies.set(DPM_ALERT_BOX_CLOSED_COOKIE_NAME, new Date().toISOString(), {
                sameSite: 'lax',
                expires: DPM_COOKIE_EXPIRATION_DAYS,
                ...(domain ? { domain } : {})
            });
        }
    }
    optOut(userSelected = false) {
        this.logMessage(LogEvent.OPT_OUT);
        this.lastOptInCheck = false;
        this.dispatchOptOut();
        this.setGlobalGroups(GLOBAL_GROUPS_OPT_OUT);
        this.writeCookie({
            gpc: this.gpcCheck(),
            optOut: true,
            selectionSource: userSelected ? 'user' : 'auto',
            timestamp: new Date().toISOString()
        });
        this.updateLegacyCookie(false);
    }
    gpcCheck() {
        // W3C GPC spec: check for navigator.globalPrivacyControl
        if (typeof navigator !== 'undefined' &&
            'globalPrivacyControl' in navigator) {
            return Boolean(navigator
                .globalPrivacyControl);
        }
        return false;
    }
    migrateLegacyCookie() {
        const legacyValue = Cookies.get(DPM_LEGACY_COOKIE_NAME);
        if (!legacyValue)
            return;
        this.logMessage(LogEvent.LEGACY_COOKIE_MIGRATION);
        // Parse key=value&key=value format
        const legacyObj = Object.fromEntries(legacyValue.split('&').map(pair => {
            const [k, v] = pair.split('=');
            return [decodeURIComponent(k), decodeURIComponent(v)];
        }));
        // Check if groups contains C0004:1
        const groups = legacyObj['groups'] || '';
        const optedIn = /C0004:1/.test(groups);
        // Write new cookie accordingly
        this.writeCookie({
            gpc: this.gpcCheck(),
            optOut: !optedIn,
            selectionSource: 'auto',
            timestamp: new Date().toISOString()
        });
    }
    /**
     * Returns the current consent state (true = opted in, false = opted out)
     */
    getConsentState() {
        // Always check the GPC signal and cookie if initialize() hasn't been called
        const gpc = this.gpcCheck();
        const savedObj = this.readCookie();
        if (gpc || savedObj?.optOut) {
            return false; // opted out
        }
        return true; // opted in (default)
    }
    dispatchOptIn() {
        this.logMessage(LogEvent.DISPATCH_OPT_IN);
        dispatchCustomEvent(CustomEventType.ONETRUST_GROUPS_UPDATED, OPT_IN_GROUPS);
        this.logMessage(LogEvent.CUSTOM_EVENT_OPT_IN);
    }
    dispatchOptOut() {
        this.logMessage(LogEvent.DISPATCH_OPT_OUT);
        dispatchCustomEvent(CustomEventType.ONETRUST_GROUPS_UPDATED, OPT_OUT_GROUPS);
        this.logMessage(LogEvent.CUSTOM_EVENT_OPT_OUT);
    }
    log() {
        // Only log when explicitly requested, not every call
        this.logMessage(LogEvent.LOG);
        return [...this.history];
    }
    reset() {
        this.history = [];
        this.lastOptInCheck = false;
        Cookies.remove(DPM_COOKIE_NAME);
    }
    readCookie() {
        const saved = Cookies.get(DPM_COOKIE_NAME);
        if (!saved) {
            return null;
        }
        try {
            // Parse key=value&key=value format
            const obj = Object.fromEntries(saved.split('&').map(pair => {
                const [k, v] = pair.split('=');
                return [decodeURIComponent(k), decodeURIComponent(v)];
            }));
            // Always return all fields, defaulting if missing
            const { gpc = 'false', optOut = 'false', selectionSource = 'auto', timestamp = '' } = obj;
            if (gpc === 'false' && optOut === 'false' && timestamp === '') {
                return null;
            }
            return {
                gpc: gpc === 'true',
                optOut: optOut === 'true',
                selectionSource: selectionSource === 'user' ? 'user' : 'auto',
                timestamp
            };
        }
        catch {
            console.error('Error reading DataPrivacyManager cookie');
            this.logMessage(LogEvent.COOKIE_READ_ERROR);
            return null;
        }
    }
    writeCookie(obj) {
        try {
            const domain = this.getCookieDomain();
            // Serialize as key=value&key=value
            const cookieValue = Object.entries(obj)
                .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
                .join('&');
            Cookies.set(DPM_COOKIE_NAME, cookieValue, {
                expires: DPM_COOKIE_EXPIRATION_DAYS,
                sameSite: 'lax',
                ...(domain ? { domain } : {})
            });
        }
        catch {
            console.error('Error writing DataPrivacyManager cookie');
            this.logMessage(LogEvent.COOKIE_WRITE_ERROR);
        }
    }
    setGlobalGroups(groups) {
        if (typeof globalThis !== 'undefined') {
            globalThis.OnetrustActiveGroups =
                groups;
        }
    }
    logMessage(message) {
        const record = {
            message,
            timestamp: new Date().toISOString()
        };
        this.history.push(record);
    }
    getLastOptInCheck() {
        return this.lastOptInCheck;
    }
}
